import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useSiteSettings, useSaveSettings } from "@/lib/siteData";
import { isSupabaseConfigured } from "@/lib/supabase";
import { Card, Field, Area, SaveBtn, Ok, Err, Notice } from "./adminUI";
import SlotsAdmin from "./SlotsAdmin";
import DateBlocksAdmin from "./DateBlocksAdmin";
import ResourcesAdmin from "./ResourcesAdmin";
import AdminsAdmin from "./AdminsAdmin";

export function ContactAdmin() {
    const settings = useSiteSettings();
    const saveSettings = useSaveSettings();
    const [form, setForm] = useState(settings);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    useEffect(() => {
        setForm(settings);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings.whatsapp_number, settings.phone_display, settings.instagram_handle]);

    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        setSaving(true);
        setErr("");
        try {
            await saveSettings({
                whatsapp_number: form.whatsapp_number,
                whatsapp_message: form.whatsapp_message,
                phone_display: form.phone_display,
                instagram_handle: form.instagram_handle,
                instagram_url: form.instagram_url,
            });
            setMsg("Salvo!");
            setTimeout(() => setMsg(""), 2500);
        } catch (ex) {
            setErr("Erro: " + ex.message);
        }
        setSaving(false);
    };

    return (
        <div className="space-y-8">
            <div>
                <h1 className="font-display text-2xl font-black uppercase sm:text-3xl">
                    WhatsApp / Contato
                </h1>
                <p className="mt-2 text-sm text-dim">
                    Fonte única para todos os botões e links do site.
                </p>
            </div>
            <Card className="space-y-4">
                <Field
                    label="Número do WhatsApp (só dígitos, com 55 + DDD)"
                    value={form.whatsapp_number}
                    onChange={set("whatsapp_number")}
                    placeholder="5517991995543"
                    testid="contact-whatsapp-number"
                />
                <Area
                    label="Mensagem padrão do WhatsApp"
                    value={form.whatsapp_message}
                    onChange={set("whatsapp_message")}
                    testid="contact-whatsapp-message"
                />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field
                        label="Telefone exibido"
                        value={form.phone_display}
                        onChange={set("phone_display")}
                        testid="contact-phone"
                    />
                    <Field
                        label="Instagram (@)"
                        value={form.instagram_handle}
                        onChange={set("instagram_handle")}
                        testid="contact-instagram-handle"
                    />
                </div>
                <Field
                    label="URL do Instagram"
                    value={form.instagram_url}
                    onChange={set("instagram_url")}
                    testid="contact-instagram-url"
                />
                <div className="flex items-center justify-end gap-4">
                    <Ok msg={msg} />
                    <Err msg={err} />
                    <SaveBtn onClick={save} saving={saving} testid="contact-save" />
                </div>
            </Card>
        </div>
    );
}

const STATUS_OPTIONS = [
    ["disponivel", "Disponível", "teal"],
    ["consulta", "Somente sob consulta", "amber"],
    ["indisponivel", "Indisponível", "red"],
];

export function AvailabilityAdmin() {
    const settings = useSiteSettings();
    const saveSettings = useSaveSettings();
    const [status, setStatus] = useState(settings.availability_status);
    const [message, setMessage] = useState(settings.availability_message);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    useEffect(() => {
        setStatus(settings.availability_status);
        setMessage(settings.availability_message);
    }, [settings.availability_status, settings.availability_message]);

    const save = async () => {
        setSaving(true);
        setErr("");
        try {
            await saveSettings({
                availability_status: status,
                availability_message: message,
            });
            setMsg("Salvo!");
            setTimeout(() => setMsg(""), 2500);
        } catch (ex) {
            setErr("Erro: " + ex.message);
        }
        setSaving(false);
    };

    return (
        <div className="space-y-8">
            <div>
                <h1 className="font-display text-2xl font-black uppercase sm:text-3xl">
                    Disponibilidade
                </h1>
                <p className="mt-2 text-sm text-dim">
                    Aviso exibido na seção de contato do site.
                </p>
            </div>
            <Card className="space-y-5">
                <div className="flex flex-wrap gap-3">
                    {STATUS_OPTIONS.map(([value, label]) => (
                        <button
                            key={value}
                            data-testid={`availability-option-${value}`}
                            onClick={() => setStatus(value)}
                            className={`rounded-full border px-5 py-2.5 font-mono2 text-[10px] font-semibold uppercase tracking-[0.2em] transition-colors ${
                                status === value
                                    ? "border-neon-cyan/60 bg-neon-cyan/10 text-neon-cyan"
                                    : "border-ink/20 text-dim hover:text-ink"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                <Area
                    label="Mensagem opcional (ex.: Consulte disponibilidade pelo WhatsApp.)"
                    value={message}
                    onChange={setMessage}
                    testid="availability-message"
                />
                <div className="flex items-center justify-end gap-4">
                    <Ok msg={msg} />
                    <Err msg={err} />
                    <SaveBtn onClick={save} saving={saving} testid="availability-save" />
                </div>
            </Card>
            <SlotsAdmin />
            <DateBlocksAdmin />
            <ResourcesAdmin />
        </div>
    );
}

export function ConfigAdmin({ role }) {
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setErr("");
        setMsg("");
        if (!password || password.length < 6) {
            setErr("A senha deve ter pelo menos 6 caracteres.");
            return;
        }
        if (password !== confirm) {
            setErr("As senhas não conferem.");
            return;
        }
        setSaving(true);
        const { error } = await supabase.auth.updateUser({ password });
        setSaving(false);
        if (error) setErr("Erro: " + error.message);
        else {
            setMsg("Senha alterada!");
            setPassword("");
            setConfirm("");
            setTimeout(() => setMsg(""), 3000);
        }
    };

    return (
        <div className="space-y-8">
            <div>
                <h1 className="font-display text-2xl font-black uppercase sm:text-3xl">
                    Configurações
                </h1>
                <p className="mt-2 text-sm text-dim">
                    Senha do administrador e status da conexão.
                </p>
            </div>
            {!isSupabaseConfigured ? (
                <Notice tone="red" testid="config-not-configured">
                    Supabase não configurado.
                </Notice>
            ) : null}
            <Card className="space-y-4">
                <p className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-neon-cyan">
                    Alterar senha do admin
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Nova senha" type="password" value={password} onChange={setPassword} testid="config-new-password" />
                    <Field label="Confirmar nova senha" type="password" value={confirm} onChange={setConfirm} testid="config-confirm-password" />
                </div>
                <div className="flex items-center justify-end gap-4">
                    <Ok msg={msg} />
                    <Err msg={err} />
                    <SaveBtn onClick={save} saving={saving} testid="config-save-password" />
                </div>
            </Card>
            <Card>
                <p className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-neon-cyan">
                    Conexão
                </p>
                <p className="mt-3 text-sm text-dim">
                    {isSupabaseConfigured
                        ? "Supabase configurado. As permissões de escrita são aplicadas pelas políticas RLS no banco — o painel depende delas, não apenas desta tela."
                        : "Supabase não configurado — o site está exibindo o conteúdo original."}
                </p>
            </Card>
            <AdminsAdmin role={role} />
        </div>
    );
}
